import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { CURRENCIES } from "@/lib/constants";
import { useKeepIdleLogoutAlive } from "@/hooks/useIdleLogout";

// Publishable key only — safe to ship in client code by design (it can
// create tokens/payment methods, never move money or read account data;
// that requires the secret key, which stays backend-only and is never
// referenced anywhere in this bundle). Loaded lazily so a build without a
// real key doesn't fail — the modal shows a "not configured" state instead.
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

/// Production funding flow:
///   1. POST /api/rails/funding -> backend creates a pending settlement +
///      a real Stripe PaymentIntent, returns only a client_secret.
///   2. Stripe.js collects and confirms the card client-side — this app
///      never sees or stores card details.
///   3. This modal only WAITS: it polls GET /api/rails/funding/{id},
///      which the backend only ever changes from its webhook handler once
///      Stripe confirms the charge server-to-server. A "successful"
///      confirmCardPayment() call here is never itself treated as proof
///      of funding — only the backend-observed status is.
export default function FundWalletModal({ open, onClose, defaultCurrency = "GBP", onFunded }) {
  if (!open) return null;
  if (!stripePromise) {
    return (
      <ModalShell onClose={onClose}>
        <p className="text-sm text-muted-foreground">
          Card funding is not configured for this deployment.
        </p>
      </ModalShell>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <FundWalletForm onClose={onClose} defaultCurrency={defaultCurrency} onFunded={onFunded} />
    </Elements>
  );
}

function ModalShell({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Add money"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md relative overflow-hidden p-7 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 w-8 h-8 rounded-full bg-secondary hover:bg-secondary/70 grid place-items-center text-muted-foreground transition-all"
        >
          <X size={16} />
        </button>
        <h2 className="font-display text-xl font-black text-navy tracking-tight">Add money</h2>
        {children}
      </div>
    </div>
  );
}

function FundWalletForm({ onClose, defaultCurrency, onFunded }) {
  const stripe = useStripe();
  const elements = useElements();

  // Stripe's CardElement below runs inside a cross-origin iframe, so
  // keystrokes typed into the card fields never reach this window's own
  // activity listeners (see useIdleLogout.js). A careful card entry that
  // takes a few minutes must never be interrupted by an idle-logout
  // warning — this keeps the shared activity clock alive for as long as
  // this modal is mounted (open).
  useKeepIdleLogoutAlive(true);

  const [stage, setStage] = useState("form"); // form | waiting | success | failure
  const [submitting, setSubmitting] = useState(false);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [amount, setAmount] = useState(100);
  const [error, setError] = useState(null);
  const [intentId, setIntentId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  const createFundingIntent = async () => {
    if (!amount || amount <= 0) {
      setError("Enter a valid amount");
      return;
    }
    setError(null);
    // `submitting` (not `stage`) drives the busy UI here — the form,
    // including <CardElement>, must stay mounted for the whole request +
    // confirmCardPayment() call below. Switching `stage` away from "form"
    // early used to unmount <CardElement>, so by the time confirmCard()
    // ran, elements.getElement(CardElement) returned null and
    // stripe.confirmCardPayment() threw a Stripe.js IntegrationError —
    // which the catch block below then mislabeled as a failed funding
    // request even though the backend had already succeeded.
    setSubmitting(true);
    // One fresh key per explicit click of "Pay" — a new intentional
    // funding attempt, not a retry of a prior one. POST /rails/funding is
    // already idempotent server-side on this header (rails_routes.py);
    // this closes the gap found in the prior audit pass where a very fast
    // double-click could otherwise create two separate PaymentIntents.
    // crypto.randomUUID() is a standard browser API (secure contexts,
    // i.e. HTTPS/localhost) — no new npm dependency needed for this.
    const idempotencyKey = `fund_${crypto.randomUUID()}`;
    try {
      const { data } = await api.post(
        "/rails/funding",
        { provider: "stripe", amount, currency, method: "card" },
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      setIntentId(data.id);
      setClientSecret(data.client_secret);
      if (!data.client_secret) {
        setError("This payment method could not be started. Please try again.");
        setSubmitting(false);
        return;
      }
      await confirmCard(data.client_secret, data.id);
    } catch (e) {
      if (e?.response) {
        // Axios error from our own backend — e.g. a raised HTTPException.
        setError(e.response?.data?.detail || "Could not start funding. Please try again.");
      } else if (e?.message) {
        // Stripe.js error (IntegrationError, StripeInvalidRequestError,
        // etc.) — these have no `.response`, so show Stripe's own message
        // instead of the backend-oriented fallback below.
        setError(e.message);
      } else {
        setError("Could not start funding. Please try again.");
      }
      setSubmitting(false);
    }
  };

  const confirmCard = async (secret, id) => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    const result = await stripe.confirmCardPayment(secret, {
      payment_method: { card: cardElement },
    });
    if (result.error) {
      setError(result.error.message || "Payment could not be confirmed.");
      setSubmitting(false);
      return;
    }
    // Same principle as the backend/mobile: a successful confirmCardPayment
    // here is Stripe telling *this browser* what happened, not proof funds
    // landed. Only the backend's webhook-driven status (polled below) can
    // move the wallet.
    setSubmitting(false);
    pollForCompletion(id);
  };

  const pollForCompletion = (id) => {
    setStage("waiting");
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/rails/funding/${id}`);
        if (data.status === "completed") {
          clearInterval(interval);
          setStage("success");
          onFunded?.();
        } else if (data.status === "failed") {
          clearInterval(interval);
          setError("The payment could not be completed.");
          setStage("failure");
        }
      } catch {
        // transient network error — keep polling
      }
      if (attempts >= 60) {
        clearInterval(interval);
        setError(
          "This is taking longer than expected. Check your wallet balance in a few minutes — " +
          "your card was charged only if the balance updates."
        );
        setStage("failure");
      }
    }, 2000);
  };

  if (stage === "waiting") {
    return (
      <ModalShell onClose={onClose}>
        <div className="flex flex-col items-center py-8 gap-3">
          <Loader2 size={28} className="animate-spin text-emerald" />
          <p className="text-sm text-muted-foreground text-center">Confirming with MoneyMeet…</p>
        </div>
      </ModalShell>
    );
  }

  if (stage === "success") {
    return (
      <ModalShell onClose={onClose}>
        <div className="flex flex-col items-center py-6 gap-3">
          <p className="text-sm text-center">
            {amount} {currency} is now available in your wallet.
          </p>
          <button onClick={onClose} className="btn-primary w-full justify-center py-3">Done</button>
        </div>
      </ModalShell>
    );
  }

  if (stage === "failure") {
    return (
      <ModalShell onClose={onClose}>
        <div className="flex flex-col items-center py-6 gap-3">
          <p className="text-sm text-danger text-center">{error}</p>
          <button
            onClick={() => { setStage("form"); setError(null); setIntentId(null); setClientSecret(null); }}
            className="btn-primary w-full justify-center py-3"
          >
            Try again
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="input-label" htmlFor="fund-cur">Currency</label>
          <select
            id="fund-cur"
            data-testid="fund-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={submitting}
            className="input-field mt-1.5 text-navy appearance-none cursor-pointer disabled:opacity-50"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="input-label" htmlFor="fund-amt">Amount</label>
          <input
            id="fund-amt"
            type="number"
            min="1"
            data-testid="fund-amount"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            disabled={submitting}
            className="input-field mt-1.5 num-tabular text-2xl font-display font-bold disabled:opacity-50"
          />
        </div>
        <div>
          <label className="input-label">Card details</label>
          <div className="input-field mt-1.5 py-3">
            <CardElement options={{ disabled: submitting, style: { base: { fontSize: "15px" } } }} />
          </div>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <button
          data-testid="fund-submit"
          onClick={createFundingIntent}
          disabled={!stripe || amount <= 0 || submitting}
          className="btn-primary w-full justify-center py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Confirming with your bank…
            </span>
          ) : (
            `Pay ${amount} ${currency}`
          )}
        </button>
      </div>
    </ModalShell>
  );
}
